import { Fragment, useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Ticket,
  Users,
  KeyRound,
  LogOut,
  KanbanSquare,
  Settings2,
  ClipboardList,
  ClipboardCheck,
  Waves,
  Menu,
  X,
  Fingerprint,
  Activity,
  Briefcase,
  BookOpen,
  ChevronDown,
  Network,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import NotificationBell from "@/components/NotificationBell";
import { api } from "@/lib/api";
import { canSeeNetwork, canManageNetwork } from "@/lib/network";
import InstallPWAButton from "@/components/InstallPWAButton";
import PushPromptBanner from "@/components/PushPromptBanner";
import AttendanceAutoCheckIn from "@/components/AttendanceAutoCheckIn";
import { Wallet, Users2, Receipt, Coins, Building2, BarChart3, Map as MapIcon, Server, Boxes, RadioTower, Split, Cable, Network as OtbIcon, ServerCog, Container } from "lucide-react";
import { hasBillingRole, canBillingManage, isKolektor as isKol } from "@/lib/billing";

const NAV = [
  // ── OPERASIONAL ───────────────────────────────────────────────
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", priv: "dashboard", sectionBefore: "OPERASIONAL" },
  { to: "/pool", label: "Klaim Tiket", icon: Waves, testid: "nav-pool", priv: "tickets" },
  { to: "/tickets", label: "Manajemen Pekerjaan", icon: Ticket, testid: "nav-tickets", priv: "tickets" },
  { to: "/kanban", label: "Status Pekerjaan", icon: KanbanSquare, testid: "nav-kanban", priv: "kanban" },
  { to: "/network/map", label: "Peta Jaringan", icon: MapIcon, testid: "nav-network-map", network: "view" },
  { to: "/network/server", label: "Master SERVER", icon: ServerCog, testid: "nav-network-server", group: "Infrastruktur", network: "view" },
  { to: "/network/olt", label: "Master OLT", icon: RadioTower, testid: "nav-network-olt", group: "Infrastruktur", network: "view" },
  { to: "/network/fdt", label: "Master FDT", icon: Container, testid: "nav-network-fdt", group: "Infrastruktur", network: "view" },
  { to: "/network/otb", label: "Master OTB", icon: OtbIcon, testid: "nav-network-otb", group: "Infrastruktur", network: "view" },
  { to: "/network/splitter", label: "Master Splitter", icon: Split, testid: "nav-network-splitter", group: "Infrastruktur", network: "view" },
  { to: "/network/odc", label: "Master ODC", icon: Server, testid: "nav-network-odc", group: "Infrastruktur", network: "manage" },
  { to: "/network/odp", label: "Master ODP", icon: Boxes, testid: "nav-network-odp", group: "Infrastruktur", network: "manage" },
  { to: "/network/cable", label: "Master Kabel", icon: Cable, testid: "nav-network-cable", group: "Infrastruktur", network: "view" },
  { to: "/report", label: "Laporkan Temuan", icon: ClipboardList, testid: "nav-report", priv: "report_create" },
  { to: "/reports-review", label: "Persetujuan Temuan", icon: ClipboardCheck, testid: "nav-reports-review", priv: "report_review", dividerAfter: true, sectionAfterDivider: "ABSENSI & LEMBUR" },
  // ── ABSENSI & LEMBUR ──────────────────────────────────────────
  { to: "/attendance", label: "Absensi", icon: Fingerprint, testid: "nav-attendance", requiresAbsensi: true },
  { to: "/attendance-requests", label: "Pengajuan Absensi", icon: Fingerprint, testid: "nav-attendance-requests" },
  { to: "/attendance-duties", label: "Dinas", icon: Briefcase, testid: "nav-attendance-duties" },
  { to: "/overtime", label: "Lembur", icon: Fingerprint, testid: "nav-overtime" },
  { to: "/attendance-report", label: "Rekap Absensi", icon: Fingerprint, testid: "nav-attendance-report", priv: "attendance_recap_view" },
  { to: "/operations", label: "Absensi & Lembur", icon: Activity, testid: "nav-operations", priv: "attendance_recap_view", dividerAfter: true, sectionAfterDivider: "BILLING" },
  // ── BILLING ───────────────────────────────────────────────────
  // Feb 2026 — "Dashboard Billing" (/billing) dihapus untuk Admin & Kolektor;
  // route tsb kini redirect ke /billing/invoices. Entry landing utama
  // Billing Kolektor ada di "Tagihan Saya", Admin ke "Invoice" / "Rekap".
  { to: "/billing/tagihan-saya", label: "Tagihan Saya", icon: Wallet, testid: "nav-billing-kolektor", billingKolektorOnly: true },
  { to: "/billing/customers", label: "Customer", icon: Users2, testid: "nav-billing-customers", billingAny: true },
  { to: "/billing/invoices", label: "Invoice", icon: Receipt, testid: "nav-billing-invoices", billingAny: true },
  { to: "/billing/rekap", label: "Rekap Billing", icon: BarChart3, testid: "nav-billing-rekap", billingAny: true },
  { to: "/billing/dashboard", label: "Dashboard Billing", icon: LayoutDashboard, testid: "nav-billing-dashboard", adminOnly: true },
  { to: "/billing/masters", label: "Master Billing", icon: Building2, testid: "nav-billing-masters", billingManage: true, dividerAfter: true, sectionAfterDivider: "ADMIN" },
  // ── ADMIN ─────────────────────────────────────────────────────
  { to: "/users", label: "Manajemen User", icon: Users, testid: "nav-users", priv: "users" },
  { to: "/app-settings", label: "Pengaturan Aplikasi", icon: Settings2, testid: "nav-app-settings", priv: "app_settings" },
  { to: "/settings", label: "Akun & Password", icon: KeyRound, testid: "nav-settings", priv: "settings" },
  // ── DOKUMENTASI ───────────────────────────────────────────────
  // Feb 2026 — Halaman Panduan Aplikasi. Read-only, semua role login
  // dapat membuka (`allRoles: true`). Route: /panduan.
  { to: "/panduan", label: "Panduan Aplikasi", icon: BookOpen, testid: "nav-panduan", allRoles: true, sectionBefore: "DOKUMENTASI" },
];

function GroupToggle({ label, open, active, onToggle, testid, mobile }) {
  return (
    <button type="button" onClick={onToggle} aria-expanded={open} data-testid={testid}
      className={`group flex w-full items-center gap-3 px-3 ${mobile ? "py-2.5 leading-5" : "py-0.5 [@media(min-height:720px)]:py-[3px] [@media(min-height:860px)]:py-1 leading-4"} rounded-lg text-[13px] font-medium transition-[background-color,color] duration-150 ${active ? "text-white" : "text-white/70"} hover:bg-white/[0.07] hover:text-white`}>
      <Network className={`h-4 w-4 flex-shrink-0 ${active ? "text-white" : "text-white/55 group-hover:text-white"}`} />
      <span className="flex-1 truncate text-left">{label}</span>
      <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
    </button>
  );
}

// Okt 2026 — Cek kesehatan Invoice Otomatis saat Admin membuka aplikasi (maks 1x / 15 menit per tab).
let lastAutoInvCheck = 0;

export default function Layout({ children }) {
  const { user, logout, hasPriv } = useAuth();
  const navigate = useNavigate();
  const isAdminUser = user?.role === "admin" || (user?.extra_roles || []).includes("admin");
  useEffect(() => {
    if (!isAdminUser || Date.now() - lastAutoInvCheck < 15 * 60 * 1000) return;
    lastAutoInvCheck = Date.now();
    api.post("/billing/auto-invoice/health-check").catch(() => {});
  }, [isAdminUser]);
  const visibleNav = NAV.filter((n) => {
    if (n.adminOnly) return user?.role === "admin";
    if (n.network) return n.network === "view" ? canSeeNetwork(user) : canManageNetwork(user);
    // Feb 2026 — Panduan Aplikasi & item lain yang dapat diakses semua
    // role login (admin/koord/teknisi/kolektor). Melewati privilege
    // check di bawah supaya tetap muncul bahkan tanpa `priv`.
    if (n.allRoles) return !!user;
    if (n.requiresAbsensi) return !!user?.absensi_enabled;
    // Fase 5.10 — Manajemen Pekerjaan (`/tickets`) is a management view for
    // admin + koordinator only. Teknisi keeps Klaim Tiket (`/pool`) and
    // Status Pekerjaan (`/kanban`) which are their operational surfaces.
    if (n.to === "/tickets" && user?.role === "teknisi") return false;
    // Billing V1 Phase 3 — role-based visibility.
    // billingAny: admin, billing, or kolektor (any billing role).
    // billingManage: admin or billing only (kolektor tidak melihat Master).
    // billingKolektorOnly: hanya user yang memiliki kolektor role (bisa juga admin/billing multi-role).
    if (n.billingAny) return hasBillingRole(user);
    if (n.billingManage) return canBillingManage(user);
    if (n.billingKolektorOnly) return isKol(user);
    return !n.priv || hasPriv(n.priv);
  });
  const { pathname } = useLocation();
  const groupActive = (g) => visibleNav.some((n) => n.group === g && pathname.startsWith(n.to));
  const [groupOpen, setGroupOpen] = useState({});
  const isOpen = (g) => groupOpen[g] ?? groupActive(g);
  const toggleGroup = (g) => setGroupOpen((x) => ({ ...x, [g]: !isOpen(g) }));
  const groupStart = (n, idx) => n.group && visibleNav[idx - 1]?.group !== n.group;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [dragX, setDragX] = useState(0); // 0..-Infinity while user swipes left
  const touchStartRef = useRef(null); // { x, y, locked: 'h'|'v'|null }

  const closeMobileMenu = () => {
    setDragX(0);
    setIsMobileMenuOpen(false);
  };

  const SWIPE_CLOSE_THRESHOLD = 70; // px to trigger close on release

  // Listen for a new Service Worker being ready and show a persistent
  // "Versi baru tersedia · Reload sekarang" banner so users can update
  // without manually clearing cache.
  useEffect(() => {
    const shownRef = { current: false };
    const onUpdate = (e) => {
      if (shownRef.current) return;
      shownRef.current = true;
      const activate = e?.detail?.activate;
      toast.message("Versi baru tersedia", {
        description: "Muat ulang untuk memakai versi terbaru aplikasi.",
        duration: Infinity,
        important: true,
        action: {
          label: "Reload sekarang",
          onClick: () => {
            if (typeof activate === "function") {
              activate();
            } else {
              window.location.reload();
            }
          },
        },
      });
    };
    window.addEventListener("sw-update-available", onUpdate);
    return () => window.removeEventListener("sw-update-available", onUpdate);
  }, []);

  const onDrawerTouchStart = (e) => {
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY, locked: null };
    setDragX(0);
  };

  const onDrawerTouchMove = (e) => {
    const start = touchStartRef.current;
    if (!start) return;
    const t = e.touches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    // Determine gesture axis on first significant move
    if (!start.locked) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      start.locked = Math.abs(dx) > Math.abs(dy) ? "h" : "v";
    }
    if (start.locked !== "h") return;
    // Only track leftward swipes (dx < 0)
    if (dx < 0) setDragX(dx);
    else setDragX(0);
  };

  const onDrawerTouchEnd = () => {
    const dx = dragX;
    touchStartRef.current = null;
    if (dx <= -SWIPE_CLOSE_THRESHOLD) {
      closeMobileMenu();
    } else {
      setDragX(0);
    }
  };

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [isMobileMenuOpen]);

  // Close drawer on Escape key
  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeMobileMenu();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isMobileMenuOpen]);

  return (
    <div className="min-h-screen flex bg-[#F8F9FA]">
      {user?.absensi_enabled && <AttendanceAutoCheckIn />}
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex flex-col w-64 bg-[#0A0A0A] text-white sticky top-0 h-screen border-r border-white/[0.06]" data-testid="sidebar-desktop">
        <div className="px-5 pt-1 [@media(min-height:860px)]:pt-2 [@media(min-height:1000px)]:pt-4 [@media(min-height:1000px)]:pb-2">
          <div className="h-8 [@media(min-height:720px)]:h-10 [@media(min-height:860px)]:h-14 [@media(min-height:1000px)]:h-16 flex items-center justify-center overflow-hidden">
            <img
              src="/logo-transparan.png"
              alt="AROMA NET"
              data-testid="sidebar-logo"
              className="h-16 [@media(min-height:720px)]:h-20 [@media(min-height:860px)]:h-28 max-w-none w-auto object-contain"
              style={{ filter: "invert(1) hue-rotate(180deg)" }}
            />
          </div>
        </div>

        <nav data-testid="sidebar-nav" className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 [@media(min-height:720px)]:pb-1 [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.15)_transparent]">
          {visibleNav.map((n, idx) => (
            <Fragment key={n.to}>
              {n.sectionBefore && (
                <div
                  data-testid={`nav-section-before-${n.testid}`}
                  className={`px-3 [@media(min-height:720px)]:pb-0.5 [@media(min-height:860px)]:pb-1 text-[10px] leading-[10px] [@media(min-height:720px)]:leading-3 font-semibold tracking-[0.14em] text-white/40 uppercase select-none ${idx > 0 ? "mt-0.5 [@media(min-height:860px)]:mt-1 pt-0.5 [@media(min-height:720px)]:pt-1 [@media(min-height:860px)]:pt-1.5 border-t border-white/[0.08]" : "pt-0.5 [@media(min-height:860px)]:pt-1"}`}
                >
                  {n.sectionBefore}
                </div>
              )}
              {groupStart(n, idx) && <GroupToggle label={n.group} open={isOpen(n.group)} active={groupActive(n.group)} onToggle={() => toggleGroup(n.group)} testid="nav-group-infrastruktur" />}
              {(!n.group || isOpen(n.group)) && <NavLink
                to={n.to}
                end
                data-testid={n.testid}
                className={({ isActive }) =>
                  `group flex items-center gap-3 px-3 ${n.group ? "pl-9" : ""} py-0.5 [@media(min-height:720px)]:py-[3px] [@media(min-height:860px)]:py-1 rounded-lg text-[13px] leading-4 font-medium transition-[background-color,color,box-shadow] duration-150 ${
                    isActive
                      ? "bg-[#0055FF] text-white shadow-[0_4px_12px_-4px_rgba(0,85,255,0.6)]"
                      : "text-white/70 hover:bg-white/[0.07] hover:text-white"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <n.icon className={`h-4 w-4 flex-shrink-0 transition-colors ${isActive ? "text-white" : "text-white/55 group-hover:text-white"}`} />
                    <span className="truncate">{n.label}</span>
                  </>
                )}
              </NavLink>}
              {n.dividerAfter && idx < visibleNav.length - 1 && (
                <div
                  role="separator"
                  aria-hidden="true"
                  data-testid={`nav-divider-${n.testid}`}
                  className="mx-3 mt-0.5 border-t border-white/[0.08] [@media(min-height:860px)]:mt-1"
                />
              )}
              {n.dividerAfter && n.sectionAfterDivider && idx < visibleNav.length - 1 && (
                <div
                  data-testid={`nav-section-${n.testid}`}
                  className="px-3 pt-0.5 [@media(min-height:720px)]:pb-0.5 [@media(min-height:720px)]:pt-1 [@media(min-height:860px)]:pt-1.5 [@media(min-height:860px)]:pb-1 text-[10px] leading-[10px] [@media(min-height:720px)]:leading-3 font-semibold tracking-[0.14em] text-white/40 uppercase select-none"
                >
                  {n.sectionAfterDivider}
                </div>
              )}
            </Fragment>
          ))}
        </nav>

        <div className="px-3 py-1 [@media(min-height:720px)]:py-1.5 [@media(min-height:860px)]:py-2.5 border-t border-white/[0.08]">
          <div className="flex items-center justify-between gap-1 rounded-lg px-2 py-0.5 [@media(min-height:720px)]:py-1 [@media(min-height:860px)]:py-1.5 bg-white/[0.04]" data-testid="sidebar-profile">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="h-8 w-8 rounded-full bg-[#0055FF] flex items-center justify-center font-display text-sm font-bold flex-shrink-0">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
              <div className="min-w-0">
                <div className="text-[13px] font-semibold truncate leading-tight" title={user?.name}>{user?.name}</div>
                <div className="text-[10px] font-semibold uppercase tracking-widest text-white/50">
                  {user?.role}
                </div>
              </div>
            </div>
            <div className="flex items-center flex-shrink-0">
              <NotificationBell />
              <button
                type="button"
                data-testid="btn-logout"
                title="Keluar"
                aria-label="Keluar"
                onClick={async () => {
                  await logout();
                  navigate("/login");
                }}
                className="p-2 rounded-lg text-white/65 hover:bg-white/[0.07] hover:text-white transition-colors"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 h-16 bg-[#0A0A0A] text-white z-[9997] flex items-center justify-between px-3 border-b border-white/[0.08]" data-testid="mobile-topbar">
        <div className="flex items-center gap-1.5 min-w-0">
          <button
            type="button"
            data-testid="btn-mobile-menu-open"
            onClick={() => setIsMobileMenuOpen(true)}
            aria-label="Buka menu"
            aria-expanded={isMobileMenuOpen}
            className="p-2 -ml-1 rounded-lg text-white/75 hover:text-white hover:bg-white/[0.07] active:bg-white/[0.1] transition-colors"
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="h-12 flex items-center justify-center overflow-hidden">
            <img
              src="/logo-transparan.png"
              alt="AROMA NET"
              data-testid="mobile-logo"
              className="h-24 max-w-none w-auto object-contain"
              style={{ filter: "invert(1) hue-rotate(180deg)" }}
            />
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <NotificationBell />
        </div>
      </div>

      {/* Mobile drawer overlay (fade) */}
      <div
        data-testid="mobile-drawer-overlay"
        onClick={closeMobileMenu}
        aria-hidden={!isMobileMenuOpen}
        className={`lg:hidden fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm transition-opacity duration-300 ease-out ${
          isMobileMenuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />

      {/* Mobile drawer (slide-in from left, swipe-left to close) */}
      <aside
        data-testid="mobile-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Menu Navigasi AROMA NET"
        onTouchStart={onDrawerTouchStart}
        onTouchMove={onDrawerTouchMove}
        onTouchEnd={onDrawerTouchEnd}
        onTouchCancel={onDrawerTouchEnd}
        style={
          isMobileMenuOpen && dragX < 0
            ? { transform: `translateX(${dragX}px)`, transition: "none" }
            : undefined
        }
        className={`lg:hidden fixed top-0 left-0 z-[9999] h-full w-72 max-w-[85vw] bg-[#0A0A0A] text-white flex flex-col shadow-2xl transform transition-transform duration-300 ease-out touch-pan-y ${
          isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="px-5 pt-3 pb-1 relative">
          <button
            type="button"
            data-testid="btn-mobile-menu-close"
            onClick={closeMobileMenu}
            aria-label="Tutup menu"
            className="absolute top-2 right-2 p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/[0.08] transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="h-16 flex items-center justify-center overflow-hidden">
            <img
              src="/logo-transparan.png"
              alt="AROMA NET"
              data-testid="drawer-logo"
              className="h-28 max-w-none w-auto object-contain"
              style={{ filter: "invert(1) hue-rotate(180deg)" }}
            />
          </div>
        </div>

        <nav className="flex-1 min-h-0 px-3 overflow-y-auto overscroll-contain pb-3 space-y-0.5" data-testid="drawer-nav">
          {visibleNav.map((n, idx) => (
            <Fragment key={n.to}>
              {n.sectionBefore && (
                <div
                  data-testid={`drawer-nav-section-before-${n.testid}`}
                  className={`px-3 pb-1 text-[10px] font-semibold tracking-[0.14em] text-white/40 uppercase select-none ${idx > 0 ? "!mt-2 pt-2.5 border-t border-white/[0.08]" : "pt-1"}`}
                >
                  {n.sectionBefore}
                </div>
              )}
              {groupStart(n, idx) && <GroupToggle mobile label={n.group} open={isOpen(n.group)} active={groupActive(n.group)} onToggle={() => toggleGroup(n.group)} testid="drawer-nav-group-infrastruktur" />}
              {(!n.group || isOpen(n.group)) && <NavLink
                to={n.to}
                end
                data-testid={`drawer-${n.testid}`}
                onClick={closeMobileMenu}
                className={({ isActive }) =>
                  `group flex items-center gap-3 px-3 ${n.group ? "pl-9" : ""} py-2.5 rounded-lg text-[13px] leading-5 font-medium transition-[background-color,color,box-shadow] duration-150 ${
                    isActive
                      ? "bg-[#0055FF] text-white shadow-[0_4px_12px_-4px_rgba(0,85,255,0.6)]"
                      : "text-white/70 hover:bg-white/[0.07] hover:text-white active:bg-white/[0.1]"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <n.icon className={`h-4 w-4 flex-shrink-0 transition-colors ${isActive ? "text-white" : "text-white/55 group-hover:text-white"}`} />
                    <span className="truncate">{n.label}</span>
                  </>
                )}
              </NavLink>}
              {n.dividerAfter && idx < visibleNav.length - 1 && (
                <div
                  role="separator"
                  aria-hidden="true"
                  data-testid={`drawer-nav-divider-${n.testid}`}
                  className="mx-3 !mt-2 border-t border-white/[0.08]"
                />
              )}
              {n.dividerAfter && n.sectionAfterDivider && idx < visibleNav.length - 1 && (
                <div
                  data-testid={`drawer-nav-section-${n.testid}`}
                  className="px-3 pt-2.5 pb-1 text-[10px] font-semibold tracking-[0.14em] text-white/40 uppercase select-none"
                >
                  {n.sectionAfterDivider}
                </div>
              )}
            </Fragment>
          ))}
        </nav>

        <div className="px-3 pt-2 pb-3 border-t border-white/[0.08]" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
          <div className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 bg-white/[0.04] min-w-0" data-testid="drawer-profile">
            <div className="h-8 w-8 rounded-full bg-[#0055FF] flex items-center justify-center font-display text-sm font-bold flex-shrink-0">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="min-w-0">
              <div className="text-[13px] font-semibold truncate leading-tight">{user?.name}</div>
              <div className="text-[10px] font-semibold uppercase tracking-widest text-white/50">
                {user?.role}
              </div>
            </div>
          </div>
          <button
            data-testid="btn-logout-mobile"
            onClick={async () => {
              closeMobileMenu();
              await logout();
              navigate("/login");
            }}
            className="mt-1 w-full flex items-center gap-3 px-3 py-2.5 text-[13px] rounded-lg text-white/65 hover:bg-white/[0.07] hover:text-white transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Keluar
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 lg:pt-0 pt-14 min-w-0">
        <PushPromptBanner />
        <div className={pathname === "/network/map" ? "max-w-[1400px] lg:max-w-none mx-auto px-4 sm:px-6 lg:px-4 py-8 lg:py-4" : "max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-8"}>{children}</div>
      </main>

      <Toaster position="top-right" richColors />
      <InstallPWAButton />
    </div>
  );
}
